/**
 * Survey Builder Page
 *
 * Allows admins to create and edit survey forms with a drag-and-drop
 * form builder interface. Supports multiple question types, validation,
 * and form settings.
 *
 * Features:
 * - Add/remove/reorder questions
 * - Multiple question types (text, select, radio, checkbox, etc.)
 * - Question validation options
 * - Form settings (title, description, public results)
 * - Save to database
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useAuth, useIsSuperAdmin } from '@/hooks/useAuth';
import { surveysApi, ApiClientError } from '@/services/api';
import { FormFieldEditor } from '@/components/admin/FormFieldEditor';
import type { SurveyQuestion, SurveyFormSchema, SurveyForm, QuestionType } from '@/types';

/**
 * Generate a unique ID for questions
 */
function generateQuestionId(): string {
  return `q_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}


/**
 * Loading spinner component
 */
function LoadingSpinner({ size = 'md' }: { size?: 'sm' | 'md' }) {
  const sizeClasses = size === 'sm' ? 'h-4 w-4' : 'h-8 w-8';

  return (
    <svg className={`animate-spin ${sizeClasses} text-primary`} viewBox="0 0 24 24">
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
        fill="none"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

/**
 * Empty state component for no questions
 */
function EmptyQuestionsState({ onAddQuestion }: { onAddQuestion: () => void }) {
  return (
    <div className="text-center py-12 border-2 border-dashed border-white/10 rounded-lg">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
        <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
          />
        </svg>
      </div>
      <h3 className="text-xl font-semibold text-white mb-2">No questions yet</h3>
      <p className="text-white/60 mb-6">Start building your survey by adding questions.</p>
      <button type="button" onClick={onAddQuestion} className="btn-primary">
        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        Add First Question
      </button>
    </div>
  );
}

/**
 * Quick add question type buttons
 */
interface QuickAddButtonsProps {
  onAdd: (type: QuestionType) => void;
}

function QuickAddButtons({ onAdd }: QuickAddButtonsProps) {
  const types: { type: QuestionType; label: string; icon: React.ReactNode }[] = [
    {
      type: 'text',
      label: 'Text',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
        </svg>
      ),
    },
    {
      type: 'textarea',
      label: 'Paragraph',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
      ),
    },
    {
      type: 'select',
      label: 'Dropdown',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
        </svg>
      ),
    },
    {
      type: 'radio',
      label: 'Choice',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" strokeWidth={2} />
          <circle cx="12" cy="12" r="4" fill="currentColor" />
        </svg>
      ),
    },
    {
      type: 'checkbox',
      label: 'Checkboxes',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ),
    },
    {
      type: 'number',
      label: 'Number',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
        </svg>
      ),
    },
    {
      type: 'date',
      label: 'Date',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      ),
    },
  ];

  return (
    <div className="flex flex-wrap gap-2">
      {types.map(({ type, label, icon }) => (
        <button
          key={type}
          type="button"
          onClick={() => onAdd(type)}
          className="flex items-center gap-2 px-3 py-2 text-sm bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded-lg text-white/80 hover:text-white transition-colors"
        >
          {icon}
          {label}
        </button>
      ))}
    </div>
  );
}

/**
 * Survey Builder Page Component
 */
export function SurveyBuilderPage() {
  const navigate = useNavigate();
  const params = useParams();
  const { isLoading: authLoading, isAuthenticated } = useAuth();
  const isSuperAdmin = useIsSuperAdmin();

  const surveyId = params.id;
  const isEditMode = Boolean(surveyId);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [questions, setQuestions] = useState<SurveyQuestion[]>([]);
  const [resultsPublic, setResultsPublic] = useState(false);
  const [isActive, setIsActive] = useState(true);

  // UI state
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  /**
   * Load existing survey for editing
   */
  const loadSurvey = useCallback(async () => {
    if (!surveyId) return;

    try {
      setIsLoading(true);
      setError(null);

      const survey = await surveysApi.get(surveyId);

      setTitle(survey.title);
      setDescription(survey.description || '');
      setQuestions(survey.formSchema.questions);
      setResultsPublic(survey.resultsPublic);
      setIsActive(survey.isActive);
      setHasChanges(false);
    } catch (err) {
      const errorMessage = err instanceof ApiClientError ? err.message : 'Failed to load survey';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [surveyId]);

  /**
   * Initial load for edit mode
   */
  useEffect(() => {
    if (!authLoading && isAuthenticated && isEditMode) {
      loadSurvey();
    }
  }, [authLoading, isAuthenticated, isEditMode, loadSurvey]);

  /**
   * Redirect non-admin users
   */
  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !isSuperAdmin)) {
      navigate('/login', { state: { from: '/admin/surveys/new' }, replace: true });
    }
  }, [authLoading, isAuthenticated, isSuperAdmin, navigate]);

  /**
   * Add a new question
   */
  const handleAddQuestion = useCallback((type: QuestionType = 'text') => {
    const newQuestion: SurveyQuestion = {
      id: generateQuestionId(),
      type,
      label: '',
      required: false,
      options: ['select', 'multiselect', 'radio', 'checkbox'].includes(type)
        ? ['Option 1', 'Option 2']
        : undefined,
    };

    setQuestions((prev) => [...prev, newQuestion]);
    setHasChanges(true);
  }, []);

  /**
   * Update a question
   */
  const handleUpdateQuestion = useCallback((index: number, updatedQuestion: SurveyQuestion) => {
    setQuestions((prev) => prev.map((q, i) => (i === index ? updatedQuestion : q)));
    setHasChanges(true);
  }, []);

  /**
   * Delete a question
   */
  const handleDeleteQuestion = useCallback((index: number) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;

    setQuestions((prev) => prev.filter((_, i) => i !== index));
    setHasChanges(true);
  }, []);

  /**
   * Move a question up
   */
  const handleMoveUp = useCallback((index: number) => {
    if (index === 0) return;

    setQuestions((prev) => {
      const newQuestions = [...prev];
      [newQuestions[index - 1], newQuestions[index]] = [newQuestions[index], newQuestions[index - 1]];
      return newQuestions;
    });
    setHasChanges(true);
  }, []);

  /**
   * Move a question down
   */
  const handleMoveDown = useCallback((index: number) => {
    setQuestions((prev) => {
      if (index === prev.length - 1) return prev;

      const newQuestions = [...prev];
      [newQuestions[index], newQuestions[index + 1]] = [newQuestions[index + 1], newQuestions[index]];
      return newQuestions;
    });
    setHasChanges(true);
  }, []);

  /**
   * Validate the form
   */
  const validateForm = useCallback((): string | null => {
    if (!title.trim()) {
      return 'Please enter a survey title';
    }

    if (questions.length === 0) {
      return 'Please add at least one question';
    }

    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (!q.label.trim()) {
        return `Question ${i + 1} needs a label`;
      }

      if (['select', 'multiselect', 'radio', 'checkbox'].includes(q.type)) {
        if (!q.options || q.options.length < 1) {
          return `Question ${i + 1} needs at least one option`;
        }
        if (q.options.some((opt) => !opt.trim())) {
          return `Question ${i + 1} has empty options`;
        }
      }
    }

    return null;
  }, [title, questions]);

  /**
   * Save the survey
   */
  const handleSave = useCallback(async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      setSuccessMessage(null);

      const formSchema: SurveyFormSchema = {
        questions,
        settings: {
          allowMultipleSubmissions: false,
          showProgressBar: questions.length > 5,
          submitButtonText: 'Submit',
        },
      };

      const surveyData = {
        title: title.trim(),
        description: description.trim() || null,
        formSchema,
        resultsPublic,
        isActive,
        groupId: null, // Global survey, not tied to a specific group
      };

      let savedSurvey: SurveyForm;

      if (isEditMode && surveyId) {
        savedSurvey = await surveysApi.update(surveyId, surveyData);
        setSuccessMessage('Survey updated successfully!');
      } else {
        savedSurvey = await surveysApi.create(surveyData);
        setSuccessMessage('Survey created successfully!');

        // Redirect to edit mode
        navigate(`/admin/surveys/${savedSurvey.id}`, { replace: true });
      }

      setHasChanges(false);

      // Clear success message after delay
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      const errorMessage = err instanceof ApiClientError ? err.message : 'Failed to save survey';
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  }, [title, description, questions, resultsPublic, isActive, isEditMode, surveyId, navigate, validateForm]);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  // Don't render anything if not authorized (will redirect)
  if (!isAuthenticated || !isSuperAdmin) {
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-display font-bold">
            <span className="text-gradient">{isEditMode ? 'Edit Survey' : 'Create Survey'}</span>
          </h1>
          <p className="text-white/60 mt-1">
            {isEditMode ? 'Modify your survey questions and settings' : 'Build a new survey form'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {hasChanges && (
            <span className="text-xs text-yellow-400 bg-yellow-400/10 px-3 py-1.5 rounded-lg">
              Unsaved changes
            </span>
          )}
          <Link to="/admin" className="btn-secondary">
            Back to Admin
          </Link>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-3">
          <svg className="w-5 h-5 text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-red-400">{error}</p>
          <button
            type="button"
            onClick={() => setError(null)}
            className="ml-auto text-red-400/60 hover:text-red-400"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Success Banner */}
      {successMessage && (
        <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-lg flex items-center gap-3">
          <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <p className="text-green-400">{successMessage}</p>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Questions */}
          <div className="lg:col-span-2 space-y-6">
            {/* Questions List */}
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white">
                  Questions ({questions.length})
                </h2>
              </div>

              {questions.length === 0 ? (
                <EmptyQuestionsState onAddQuestion={() => handleAddQuestion('text')} />
              ) : (
                <div className="space-y-4">
                  {questions.map((question, index) => (
                    <FormFieldEditor
                      key={question.id}
                      field={question}
                      index={index}
                      totalFields={questions.length}
                      onChange={(updated) => handleUpdateQuestion(index, updated)}
                      onDelete={() => handleDeleteQuestion(index)}
                      onMoveUp={() => handleMoveUp(index)}
                      onMoveDown={() => handleMoveDown(index)}
                    />
                  ))}
                </div>
              )}

              {/* Quick Add Buttons */}
              {questions.length > 0 && (
                <div className="mt-6 pt-6 border-t border-white/10">
                  <p className="text-sm text-white/60 mb-3">Add Question:</p>
                  <QuickAddButtons onAdd={handleAddQuestion} />
                </div>
              )}
            </div>
          </div>

          {/* Sidebar - Settings */}
          <div className="lg:col-span-1 space-y-6">
            {/* Survey Details */}
            <div className="card">
              <h2 className="text-lg font-semibold text-white mb-4">Survey Details</h2>

              {/* Title */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Title <span className="text-primary">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    setHasChanges(true);
                  }}
                  className="input"
                  placeholder="Survey title..."
                />
              </div>

              {/* Description */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-white/80 mb-2">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    setHasChanges(true);
                  }}
                  className="input min-h-[100px] resize-y"
                  placeholder="Describe your survey..."
                />
              </div>
            </div>

            {/* Settings */}
            <div className="card">
              <h2 className="text-lg font-semibold text-white mb-4">Settings</h2>

              {/* Active Toggle */}
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-white/10">
                <div>
                  <p className="text-sm font-medium text-white/80">Active</p>
                  <p className="text-xs text-white/40">Allow responses to this survey</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setIsActive(!isActive);
                    setHasChanges(true);
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    isActive ? 'bg-primary' : 'bg-white/20'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      isActive ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Public Results Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-white/80">Public Results</p>
                  <p className="text-xs text-white/40">Allow respondents to see results</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setResultsPublic(!resultsPublic);
                    setHasChanges(true);
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    resultsPublic ? 'bg-primary' : 'bg-white/20'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      resultsPublic ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Save Button */}
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving || !hasChanges}
              className="w-full btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSaving ? (
                <>
                  <LoadingSpinner size="sm" />
                  Saving...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                    />
                  </svg>
                  {isEditMode ? 'Save Changes' : 'Create Survey'}
                </>
              )}
            </button>

            {/* Preview Link (for edit mode) */}
            {isEditMode && surveyId && (
              <Link
                to={`/surveys/${surveyId}`}
                className="w-full btn-secondary py-3 flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
                Preview Survey
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SurveyBuilderPage;
